<template>
  <div class="app-header">
    <var-row align="center" justify="space-between" style="width: 100%; padding: 2px 16px;">
      <!-- 左侧：公司名称 + 会员信息 -->
      <var-col v-if="can('companyName') && appStore.currentUserInfo?.company" :span="$isDesktop ? 4 : 6"
        justify="flex-start">
        <div class="company-info">
          <var-avatar v-if="appStore.currentUserInfo?.company.logo"
            :src="$assetUrl(appStore.currentUserInfo?.company.logo)" size="32" />
          <var-avatar v-else size="32" style="background:#f5f7fa;color:#bbb;font-size:10px;">无图</var-avatar>
          <h3>{{ companyDisplayName }}</h3>
          <template v-if="appStore.currentUserInfo?.role === 'admin'">
            <div @click="goToRenew" style="cursor: pointer;">
              <var-chip type="primary" size="mini">{{ membershipPlanText }}</var-chip>
              <span v-if="membershipDaysRemaining !== null && membershipStatus !== 'expired'" class="membership-days">
                {{ membershipDaysRemaining }}天
              </span>
              <span v-if="membershipExpireAt" class="membership-expire">
                到期: {{ formatDate(membershipExpireAt) }}
              </span>
            </div>
          </template>
        </div>
      </var-col>

      <!-- 中间：Tabs -->
      <var-col :span="$isDesktop ? 16 : 22" justify="center">
        <var-tabs v-if="visibleTabs.length > 0" v-model:active="activeTab" @change="switchTab">
          <var-tab v-for="tab in visibleTabs" :key="tab.key" :name="tab.key">
            {{ tab.label }}
          </var-tab>
        </var-tabs>
        <div v-else
          style="display:flex;align-items:center;justify-content:center;padding:8px 0;color:#f56c6c;font-size:14px;">
          <var-icon name="information" size="18" color="#f56c6c" style="margin-right:6px;" />
          <span>您没有任何模块的查看权限，请联系管理员</span>
        </div>
      </var-col>

      <!-- 右侧：用户 + 设置 -->
      <var-col :span="$isDesktop ? 4 : 2" justify="flex-end">
        <var-button v-if="canAccessSettings" size="mini" text outline @click="openSettings">
          <var-icon name="cog" size="small" />
          <span v-if="$isDesktop">{{ appStore.currentUser }}</span>
        </var-button>
        <span v-else-if="$isDesktop" style="font-size:13px;color:#666;">
          {{ appStore.currentUser }}
        </span>
      </var-col>
    </var-row>

    <!-- 设置弹窗 -->
    <var-popup v-model:show="settingsVisible" :position="isDesktop ? 'right' : 'bottom'"
      :style="isDesktop ? '' : 'border-radius:12px 12px 0 0;overflow:hidden;'">
      <SettingsPanel @close="settingsVisible = false" />
    </var-popup>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useAppStore } from '@/stores'
import { useFeature } from '@/composables/useFeature'
import { useDevice } from '@/composables/useDevice'
import { MEMBERSHIP_PLAN_MAP } from '@/config/deviceFeatures'
import SettingsPanel from '@/components/settings/SettingsPanel.vue'

const router = useRouter()
const route = useRoute()
const appStore = useAppStore()
const { isDesktop } = useDevice()

const settingsVisible = ref(false)

const { can, hasAnyPermission } = useFeature('other')

const membershipInfo = computed(() => {
  return appStore.currentUserInfo?.company || null
})

const TAB_PERMISSIONS = {
  sale: ['sale_view'],
  production: ['production_view'],
  styles: ['style_view'],
  processes: ['process_view'],
  materials: ['material_view'],
  customer: ['customer_view'],
  bookkeeping: ['bookkeeping_view'],
}

const companyDisplayName = computed(() => {
  const company = appStore.currentUserInfo?.company
  if (!company) return ''
  if (typeof company === 'object' && company.name) {
    return company.name
  }
  if (typeof company === 'string') {
    return company
  }
  return ''
})

const membershipStatus = computed(() => {
  return membershipInfo.value?.membership_status || 'active'
})

const membershipPlanText = computed(() => {
  const plan = membershipInfo.value?.membership_plan
  return MEMBERSHIP_PLAN_MAP[plan] || plan || '基础版'
})

const membershipDaysRemaining = computed(() => {
  const days = membershipInfo.value?.days_remaining
  if (days === null || days === undefined) return null
  return Math.ceil(days)
})

const membershipExpireAt = computed(() => {
  return membershipInfo.value?.membership_expire_at
})

function formatDate(dateStr) {
  if (!dateStr) return ''
  const date = new Date(dateStr)
  return date.toLocaleDateString('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  })
}

function goToRenew() {
  router.push('/membership/renew')
}

const visibleTabs = computed(() => {
  return appStore.moduleTabs.filter(tab => {
    const perms = TAB_PERMISSIONS[tab.key]
    if (!perms || perms.length === 0) return true
    return hasAnyPermission(perms)
  })
})

// ============================================================
// ✅ 修复：支持前缀匹配（如 /production/analysis 归到 production）
// ============================================================
const activeTab = computed({
  get: () => {
    if (route.path === '/membership/renew') {
      return ''
    }

    if (visibleTabs.value.length === 0) {
      return ''
    }

    const path = route.path.replace('/', '') || 'sale'

    // 1. 精确匹配
    let matched = visibleTabs.value.find(tab => tab.key === path)

    // 2. ✅ 前缀匹配：/production/analysis → production
    if (!matched) {
      matched = visibleTabs.value.find(tab =>
        route.path.startsWith(`/${tab.key}/`) || route.path === `/${tab.key}`
      )
    }

    if (matched) {
      return matched.key
    }

    // 3. 完全不在任何 tab 下，才跳回第一个 tab
    const firstVisible = visibleTabs.value[0].key
    if (route.path !== `/${firstVisible}` && route.path !== '/') {
      setTimeout(() => {
        router.push(`/${firstVisible}`)
      }, 0)
    }
    return firstVisible
  },
  set: () => { }
})

const canAccessSettings = computed(() => {
  return hasAnyPermission([
    'system_modify_password',
    'system_user_manage',
    'system_manage_platform',
    'system_create_backup',
    'system_restore_backup',
  ])
})

function switchTab(key) {
  if (key === activeTab.value) return
  router.push(`/${key}`)
}

function openSettings() {
  settingsVisible.value = true
}
</script>

<style scoped>
.app-header {
  background-color: white;
  border-bottom: 1px solid #f0f0f0;
}

.company-info {
  display: flex;
  align-items: center;
  gap: 10px;
}

.membership-expire {
  font-size: 11px;
  color: #999;
}

.settings-popup {
  width: 400px;
  max-width: 90vw;
  height: 100vh;
  background: #fff;
  padding: 0;
}

@media (max-width: 768px) {
  .settings-popup {
    width: 100vw;
    max-width: 100vw;
    border-radius: 12px 12px 0 0;
    overflow: hidden;
  }

  .membership-expire {
    font-size: 10px;
  }
}
</style>